import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, ChevronUp, ChevronDown, Loader2, CheckCircle2, XCircle, Check } from "lucide-react";

/** playbookOnly: just the Rogers Brain editor, always open (Lead Desk → Settings → Rogers Brain playbook). */
export default function RogersOpsPanel({ wsId, playbookOnly = false }: { wsId: number | null; playbookOnly?: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(playbookOnly);
  const [brain, setBrain] = useState("");
  const [brainDirty, setBrainDirty] = useState(false);

  const { data: readiness, isLoading: readinessLoading } = useQuery<any>({
    queryKey: ["/api/workspaces", wsId, "rogers-readiness"],
    enabled: !!wsId && open,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/rogers-readiness`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load readiness");
      return res.json();
    },
  });

  const { data: brainData } = useQuery<any>({
    queryKey: ["/api/workspaces", wsId, "rogers-brain"],
    enabled: !!wsId && open,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/rogers-brain`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Rogers Brain");
      return res.json();
    },
  });

  useEffect(() => {
    if (brainData && !brainDirty) setBrain(brainData.brain || "");
  }, [brainData, brainDirty]);

  const saveBrain = useMutation({
    mutationFn: async (value: string) => {
      const res = await apiRequest("PATCH", `/api/workspaces/${wsId}/rogers-brain`, { brain: value });
      return res.json();
    },
    onSuccess: () => {
      setBrainDirty(false);
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "rogers-brain"] });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "rogers-readiness"] });
      toast({ title: "Rogers Brain saved", description: "Rogers will use this playbook on every run." });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const checks: { key: string; label: string; ok: boolean; detail?: string }[] = readiness ? [
    { key: "booking", label: "Booking destination", ok: !!readiness.booking?.ok, detail: readiness.booking?.ok ? (readiness.booking.usingExternal ? "External link" : "Built-in booking page") : "No booking link — Rogers can't schedule calls" },
    { key: "reply", label: "Inbound reply address", ok: !!readiness.replyDomain?.ok, detail: readiness.replyDomain?.address || "Reply token missing" },
    { key: "email", label: "Email sending (SendGrid)", ok: !!readiness.email?.ok, detail: readiness.email?.ok ? "Configured" : "Not configured — emails won't send" },
    { key: "sms", label: "SMS sending (Twilio)", ok: !!readiness.sms?.ok, detail: readiness.sms?.ok ? "Configured" : "Optional — not configured" },
    { key: "brain", label: "Rogers Brain playbook", ok: !!readiness.brain?.ok, detail: readiness.brain?.ok ? "Custom playbook set" : "Using default starter playbook" },
  ] : [];

  const criticalFails = checks.filter(c => !c.ok && c.key !== "sms" && c.key !== "brain").length;

  return (
    <div className="rounded-2xl border border-border bg-card/50 overflow-hidden" data-testid="rogers-ops-card">
      {!playbookOnly && <button
        data-testid="button-toggle-rogers-ops"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-sky-500/10">
            <Sparkles size={16} className="text-sky-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Rogers Setup &amp; Readiness</p>
            <p className="text-xs text-muted-foreground/70">Booking, replies, and your sales playbook</p>
          </div>
          {open && criticalFails > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400" data-testid="badge-rogers-issues">{criticalFails} issue{criticalFails > 1 ? "s" : ""}</span>
          )}
        </div>
        {open ? <ChevronUp size={16} className="text-muted-foreground/60" /> : <ChevronDown size={16} className="text-muted-foreground/60" />}
      </button>}
      {open && (
        <div className={playbookOnly ? "p-4" : "px-4 pb-4 space-y-5 border-t border-border pt-4"}>
          {!playbookOnly && <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Operational Readiness</label>
            {readinessLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground/60"><Loader2 size={12} className="animate-spin" /> Checking…</div>
            ) : (
              <div className="space-y-1.5" data-testid="rogers-readiness-list">
                {checks.map((c) => (
                  <div key={c.key} className="flex items-start gap-2.5 text-sm" data-testid={`readiness-${c.key}`}>
                    {c.ok ? <CheckCircle2 size={15} className="text-emerald-400 mt-0.5 shrink-0" /> : <XCircle size={15} className={`mt-0.5 shrink-0 ${c.key === "sms" || c.key === "brain" ? "text-amber-400" : "text-red-400"}`} />}
                    <div className="min-w-0">
                      <p className="text-foreground/90 font-medium leading-tight">{c.label}</p>
                      {c.detail && <p className="text-[11px] text-muted-foreground/60 truncate">{c.detail}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>}

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rogers Brain — Sales Playbook</label>
              {brainData?.default && (
                <button
                  data-testid="button-load-default-brain"
                  onClick={() => { setBrain(brainData.default); setBrainDirty(true); }}
                  className="text-[11px] text-sky-400 hover:underline"
                >Load starter template</button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground/60">Tell Rogers how to sell for you — voice, offer, objections, FAQs. Loaded into every message and reply.</p>
            <textarea
              data-testid="textarea-rogers-brain"
              value={brain}
              onChange={(e) => { setBrain(e.target.value); setBrainDirty(true); }}
              rows={10}
              placeholder="Describe your business, offer, voice, common objections, and what a great outcome looks like…"
              className="w-full text-[12px] bg-muted/40 border border-border rounded-xl px-3 py-2.5 text-foreground/90 font-mono leading-relaxed resize-y focus:outline-none focus:ring-1 focus:ring-sky-500/40"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground/50">{brain.length}/8000</span>
              <button
                data-testid="button-save-rogers-brain"
                disabled={!brainDirty || saveBrain.isPending}
                onClick={() => saveBrain.mutate(brain.slice(0, 8000))}
                className="flex items-center gap-2 px-4 py-1.5 bg-sky-500/10 border border-sky-500/20 text-sky-400 hover:bg-sky-500/20 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40"
              >
                {saveBrain.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Save Playbook
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
